import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Card, Tabs, Table, Button, Select, Empty, message, Tooltip } from "antd";
import { DownloadOutlined, EyeOutlined } from "@ant-design/icons";
import * as XLSX from "xlsx";
import moment from "moment";
import { getSubToolbox, getToolboxDetailsForWeb, getAllEquipmentDetails, getPreSignedUrl } from "./educationApi";

const { TabPane } = Tabs;
const { Option } = Select;

// Confirmed by reading reports/educationRegister/index.js directly — NOT
// the generic dump I first built from the route/constant map alone. Status
// filter is purely client-side (over already-fetched data), not a new call.
export const EducationReportTab = () => {
  const [options, setOptions] = useState([]);
  const [toolboxId, setToolboxId] = useState(undefined);
  const [status, setStatus] = useState(undefined);
  const [allData, setAllData] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      const { error, result } = await getSubToolbox(2);
      setOptions(!error && Array.isArray(result?.result) ? result.result : []);
    })();
  }, []);

  const onOptionChange = useCallback(async (value) => {
    setToolboxId(value);
    setStatus(undefined);
    if (!value) { setAllData([]); return; }
    setLoading(true);
    const title = options.find((o) => o.id === value)?.title;
    const { error, result } = await getToolboxDetailsForWeb(value, title);
    setAllData(!error && Array.isArray(result?.result) ? result.result : []);
    setLoading(false);
  }, [options]);

  const rows = useMemo(() => {
    if (status === undefined) return allData;
    const wantTrue = status === "yes";
    return allData.filter((item) => Boolean(item.status) === wantTrue);
  }, [allData, status]);

  const exportExcel = () => {
    const data = rows.map((item) => ({ "User Name": item.user_name, State: item.state_name, Status: item.status === true ? "Yes" : "No" }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Report");
    XLSX.writeFile(wb, "Monthly Education Report.xlsx");
  };

  const columns = [
    { title: "User Name", dataIndex: "user_name", key: "user_name" },
    { title: "State", dataIndex: "state_name", key: "state_name" },
    { title: "Status", dataIndex: "status", key: "status", render: (v) => (v ? "Yes" : "No") },
  ];

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 16, alignItems: "flex-end", marginBottom: 16 }}>
        <Select allowClear showSearch optionFilterProp="children" placeholder="Select Option" style={{ width: 240 }} value={toolboxId} onChange={onOptionChange}>
          {options.map((o) => <Option key={o.id} value={o.id}>{o.title}</Option>)}
        </Select>
        <Select allowClear placeholder="Status" style={{ width: 140 }} value={status} onChange={setStatus}>
          <Option value="yes">Yes</Option>
          <Option value="no">No</Option>
        </Select>
        {rows.length > 0 && <Button icon={<DownloadOutlined />} onClick={exportExcel}>Excel</Button>}
      </div>
      {rows.length === 0 && !loading ? (
        <Empty description={toolboxId ? "No education report data." : "Choose an education above to see who has completed it."} />
      ) : (
        <Table rowKey={(r, i) => i} columns={columns} dataSource={rows} loading={loading} pagination={{ pageSize: 20 }} />
      )}
    </div>
  );
};

// Confirmed by reading reports/equipment/index.js — all three filters are
// derived client-side from the already-fetched data (unique facility/serial/
// date values), not separate API calls; filtering itself is also client-side.
export const EquipmentReportTab = () => {
  const [allData, setAllData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [facility, setFacility] = useState(undefined);
  const [serial, setSerial] = useState(undefined);
  const [testTagDate, setTestTagDate] = useState(undefined);
  const [viewingRow, setViewingRow] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const { error, result } = await getAllEquipmentDetails();
    const data = !error && Array.isArray(result?.result) ? result.result : [];
    data.sort((a, b) => (a.facility_name || "").localeCompare(b.facility_name || ""));
    setAllData(data);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const uniqueSorted = (key) => Array.from(new Set(allData.map((d) => d[key]).filter(Boolean))).sort();

  const rows = allData.filter((d) =>
    (!facility || (d.facility_name || "").toLowerCase().includes(facility.toLowerCase())) &&
    (!serial || (d.serial_no || "").toLowerCase().includes(serial.toLowerCase())) &&
    (!testTagDate || d.test_tag_date === testTagDate)
  );

  const handleView = async (row) => {
    setViewingRow(row);
    const { error, result } = await getPreSignedUrl(row.pdf_link);
    if (!error && result?.result) window.open(result.result, "_blank");
    else message.error("Could not open the PDF.");
    setViewingRow(null);
  };

  const columns = [
    { title: "Facility Name", dataIndex: "facility_name", key: "facility_name" },
    { title: "User Name", dataIndex: "user_name", key: "user_name" },
    { title: "Equipment", dataIndex: "equipment", key: "equipment" },
    { title: "Serial no.", dataIndex: "serial_no", key: "serial_no" },
    {
      title: "Test & Tag Date",
      dataIndex: "test_tag_date",
      key: "test_tag_date",
      render: (v) => (v ? moment(v, "DD-MM-YYYY").format("DD-MM-YYYY") : "-"),
    },
    {
      title: "Action",
      key: "action",
      render: (_, row) => (
        <Tooltip title="View"><Button size="small" icon={<EyeOutlined />} loading={viewingRow === row} onClick={() => handleView(row)} aria-label="View" /></Tooltip>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: "flex", gap: 16, alignItems: "flex-end", flexWrap: "wrap", marginBottom: 16 }}>
        <div>
          <div style={{ marginBottom: 4, fontSize: 13, color: "#5B6472" }}>Facility</div>
          <Select allowClear showSearch placeholder="Select Facility" style={{ width: 200 }} value={facility} onChange={setFacility}>
            {uniqueSorted("facility_name").map((v) => <Option key={v} value={v}>{v}</Option>)}
          </Select>
        </div>
        <div>
          <div style={{ marginBottom: 4, fontSize: 13, color: "#5B6472" }}>Serial Number</div>
          <Select allowClear showSearch placeholder="Select Serial Number" style={{ width: 200 }} value={serial} onChange={setSerial}>
            {uniqueSorted("serial_no").map((v) => <Option key={v} value={v}>{v}</Option>)}
          </Select>
        </div>
        <div>
          <div style={{ marginBottom: 4, fontSize: 13, color: "#5B6472" }}>Test & Tag Date</div>
          <Select allowClear showSearch placeholder="Select Test & Tag Date" style={{ width: 200 }} value={testTagDate} onChange={setTestTagDate}>
            {uniqueSorted("test_tag_date").map((v) => <Option key={v} value={v}>{moment(v, "DD-MM-YYYY").format("DD-MM-YYYY")}</Option>)}
          </Select>
        </div>
      </div>
      {rows.length === 0 && !loading ? (
        <Empty description="No equipment data." />
      ) : (
        <Table rowKey={(r, i) => i} columns={columns} dataSource={rows} loading={loading} pagination={{ pageSize: 20 }} scroll={{ x: true }} />
      )}
    </div>
  );
};

const Reports = () => (
  <Card bodyStyle={{ padding: "24px 28px" }}>
    <Tabs defaultActiveKey="education">
      <TabPane tab="Education" key="education">
        <EducationReportTab />
      </TabPane>
      <TabPane tab="Equipments" key="equipment">
        <EquipmentReportTab />
      </TabPane>
    </Tabs>
  </Card>
);

export default Reports;
